import React from 'react';

import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import FilePickerDialog from 'pages/devdocs/article/FilePickerDialog';
import { WorkSpaceNode, getFileDetail } from 'stores/WorkSpaceStoreAdapter';

/** FileSelect が確定・解決したファイル情報 */
export type SelectedFile = {
  fileId: string;
  name: string;
  extension: string;
  /** 絶対パス（例: /docs/capital/article.knz） */
  path: string;
};

type Props = {
  /** 選択中ファイルの file_id（内部保持。画面には表示しない）。未選択は空文字 */
  fileId: string;
  /**
   * 選択中ファイルの絶対パス（表示用）。
   * 空 かつ fileId がある場合（初期値として fileId のみ渡された場合）は、
   * ファイル取得API（API6106）で非同期にパスを解決し、その間サークルを表示する。
   */
  path: string;
  /** ワークスペースの全ノード（ファイル選択ダイアログのツリー表示用） */
  nodes: WorkSpaceNode[];
  /** ノード取得中フラグ（ダイアログのローディング表示用） */
  nodesLoading: boolean;
  /** 選択可能なファイル拡張子（小文字。例: ['md','knz']） */
  selectableExtensions: string[];
  /** ファイル確定・パス解決時に呼ばれる */
  onChange: (file: SelectedFile) => void;
  /** ラベル */
  label?: string;
  /** 必須表示 */
  required?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /**
   * 固定（変更不可）。true のとき、クリックしてもダイアログを開かない。
   * 編集モードなどでファイルを変更させない用途。
   */
  locked?: boolean;
};

/**
 * 本文ファイル選択コンポーネント。
 *
 * - クリックすると `FilePickerDialog`（ワークスペースのツリー）を開き、ファイルを選択する。
 * - 表示は「絶対パス」。file_id は内部で保持し、画面には出さない。
 * - 初期値として fileId のみが渡され path が空の場合は、ファイル取得API（API6106）で
 *   パスを非同期に解決し、その間はサークル（CircularProgress）を表示する。
 *   取得に失敗した場合（他人ファイルで 422 等）はパス解決をあきらめ、fileId 表示にフォールバックする。
 */
const FileSelect: React.FC<Props> = ({
  fileId,
  path,
  nodes,
  nodesLoading,
  selectableExtensions,
  onChange,
  label = '本文ファイル',
  required = false,
  error = false,
  helperText,
  locked = false,
}) => {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  /** fileId のみ渡されたときに API で解決したパス */
  const [resolvedPath, setResolvedPath] = React.useState('');
  /** パス解決中（サークル表示） */
  const [resolving, setResolving] = React.useState(false);

  // 初期値として fileId のみ（path 空）で渡された場合、API でパスを解決する。
  React.useEffect(() => {
    // path が親から与えられている場合は解決不要
    if (path) {
      setResolvedPath('');
      return;
    }
    if (!fileId) {
      setResolvedPath('');
      return;
    }
    let cancelled = false;
    setResolving(true);
    getFileDetail(fileId)
      .then((detail) => {
        if (!cancelled) setResolvedPath(detail.path ?? '');
      })
      .catch(() => {
        // 取得失敗（他人ファイルの 422 等）はパス表示をあきらめる。fileId 表示にフォールバック
        if (!cancelled) setResolvedPath('');
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, path]);

  // 表示値: 親から渡された path 優先 → API 解決 path →（フォールバック）fileId
  const displayValue = path || resolvedPath || (fileId && !resolving ? fileId : '');

  const handleSelect = (file: SelectedFile) => {
    // 選択時は path が確定するため、解決結果はクリアして親の path を優先させる
    setResolvedPath('');
    onChange(file);
  };

  return (
    <>
      <TextField
        label={label}
        size="small"
        required={required}
        fullWidth
        value={displayValue}
        placeholder={locked ? '' : 'クリックしてファイルを選択'}
        error={error}
        helperText={helperText}
        onClick={() => !locked && setPickerOpen(true)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <FolderOpenIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: resolving ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            cursor: locked ? 'default' : 'pointer',
          },
          '& .MuiOutlinedInput-input': { cursor: locked ? 'default' : 'pointer' },
        }}
      />

      {!locked && (
        <FilePickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          nodes={nodes}
          loading={nodesLoading}
          selectableExtensions={selectableExtensions}
          onSelect={handleSelect}
        />
      )}
    </>
  );
};

export default FileSelect;
